/*
 This file is part of GNU Taler
 (C) 2021-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

/**
 *
 * @author Sebastian Javier Marchano (sebasjm)
 */

import {
  assertUnreachable,
  HttpStatusCode,
  TalerMerchantApi,
} from "@gnu-taler/taler-util";
import {
  ButtonBetterBulma,
  LocalNotificationBannerBulma,
  useLocalNotificationBetter,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { Fragment, h, VNode } from "preact";
import { useState } from "preact/hooks";
import {
  FormErrors,
  FormProvider,
} from "../../../../components/form/FormProvider.js";
import { Input } from "../../../../components/form/Input.js";
import { useSessionContext } from "../../../../context/session.js";
import { WithId } from "../../../../declaration.js";
import { NotificationCardBulma } from "@gnu-taler/web-util/browser";
import { undefinedIfEmpty } from "../../../../utils/table.js";
import { InputSelector } from "../../../../components/form/InputSelector.js";
import { InputDurationSelector } from "../../../../components/form/InputDurationSelector.js";
import { Tooltip } from "../../../../components/Tooltip.js";
import { InputDurationDropdown } from "../../../../components/form/InputDurationDropdown.js";

const TALER_SCREEN_ID = 39;

type Entity = TalerMerchantApi.ReportDetailResponse & WithId;

const mimeTypes = [
  undefined,
  "application/json",
  "application/pdf",
  "text/html",
  "text/csv",
  "application/xml",
  "application/zip",
  "image/png",
  "image/jpeg",
] as const;

type MimeTypes = (typeof mimeTypes)[number];

interface Props {
  onUpdated: () => void;
  onBack?: () => void;
  report: Entity;
}
export function UpdatePage({ report, onUpdated, onBack }: Props): VNode {
  const { i18n } = useTranslationContext();
  const {
    state: { token },
    lib,
    config,
  } = useSessionContext();
  const noGenerators = !config.report_generators.length;
  const forcedGenerator = config.report_generators.length === 1;
  // FIXME: if the product list is big the will bring a lot of info
  // const inventoryResult = useInstanceProducts();

  // const inventory =
  //   !inventoryResult ||
  //     inventoryResult instanceof TalerError ||
  //     inventoryResult.type === "fail"
  //     ? []
  //     : inventoryResult.body;

  const [state, setState] = useState<Partial<Entity>>({
    ...report,
  });

  // useEffect(() => {
  //   if (!category || !category?.products || !token) return;
  //   const ps = category.products.map((prod) => {
  //     return lib.instance
  //       .getProductDetails(token, String(prod.product_id))
  //       .then((res) => {
  //         return res.type === "fail"
  //           ? undefined
  //           : {
  //               id: String(prod.product_id),
  //               description: res.body.description,
  //             };
  //       });
  //   });
  //   Promise.all(ps).then((all) => {
  //     const product_map = all.filter(notEmpty);
  //     setState({ ...state, product_map });
  //   });
  // }, []);
  const errors = undefinedIfEmpty<FormErrors<Entity>>({
    description: !state.description ? i18n.str`Required` : undefined,
    data_source: !state.data_source
      ? i18n.str`Required`
      : !state.data_source.startsWith("/")
        ? i18n.str`It should start with /`
        : undefined,
    mime_type: !state.mime_type ? i18n.str`Required` : undefined,
    program_section: !state.program_section ? i18n.str`Required` : undefined,
    report_frequency: !state.report_frequency ? i18n.str`Required` : undefined,
    report_frequency_shift: !state.report_frequency_shift
      ? i18n.str`Required`
      : undefined,
    target_address: !state.target_address ? i18n.str`Required` : undefined,
  });

  const hasErrors = errors !== undefined;

  const [notification, safeFunctionHandler] = useLocalNotificationBetter();
  const data = state as TalerMerchantApi.ReportAddRequest;
  const update = safeFunctionHandler(
    i18n.str`update scheduled report`,
    lib.instance.updateScheduledReport.bind(lib.instance),
    !token || hasErrors ? undefined : [token, report.id, data],
  );
  update.onSuccess = onUpdated;
  update.onFail = (fail) => {
    switch (fail.case) {
      case HttpStatusCode.Unauthorized:
        return i18n.str`Unauthorized`;
      case HttpStatusCode.NotFound:
        return i18n.str`Not found`;
      default:
        assertUnreachable(fail);
    }
  };

  if (noGenerators) {
    return (
      <NotificationCardBulma
        notification={{
          message: i18n.str`No report generator configured in the server`,
          description: i18n.str`Contact the system administrator to create a report generator before scheduling one.`,
          type: "WARN",
        }}
      />
    );
  }
  return (
    <div>
      <LocalNotificationBannerBulma notification={notification} />
      <section class="section">
        <section class="hero is-hero-bar">
          <div class="hero-body">
            <div class="level">
              <div class="level-left">
                <div class="level-item">
                  <span class="is-size-4">
                    <i18n.Translate>Id:</i18n.Translate>
                    &nbsp;
                    <b>{report.id}</b>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>
        <hr />

        <section class="section is-main-section">
          <div class="columns">
            <div class="column is-four-fifths">
              <FormProvider
                object={state}
                valueHandler={setState}
                errors={errors}
              >
                <Input<Entity>
                  name="description"
                  label={i18n.str`Description`}
                  help={i18n.str`Description of the report. Possibly included in the report message`}
                />

                <Input<Entity>
                  name="target_address"
                  label={i18n.str`Address`}
                  help={i18n.str`Where the report program should send the report`}
                />

                <Input<Entity>
                  name="data_source"
                  label={i18n.str`Source`}
                  help={i18n.str`Base URL to request the data from.`}
                />

                <InputSelector<Entity>
                  name="mime_type"
                  label={i18n.str`Type`}
                  help={i18n.str`Type of the data source`}
                  values={mimeTypes as any}
                  toStr={(v: MimeTypes) => {
                    switch (v) {
                      case undefined:
                        return i18n.str`Choose one`;
                      default: {
                        return v;
                      }
                    }
                  }}
                />

                <InputSelector<Entity>
                  name="program_section"
                  label={i18n.str`Program`}
                  readonly={forcedGenerator}
                  help={i18n.str`Merchant backend configuration section specifying the program to use to transmit the report`}
                  values={config.report_generators}
                />

                <InputDurationDropdown<Entity>
                  name="report_frequency"
                  label={i18n.str`Report frequency`}
                  useProtocolDuration
                />

                <InputDurationDropdown<Entity>
                  name="report_frequency_shift"
                  label={i18n.str`Report frequency shift`}
                  useProtocolDuration
                />

                <div class="buttons is-right mt-5">
                  {onBack && (
                    <button class="button" onClick={onBack}>
                      <i18n.Translate>Cancel</i18n.Translate>
                    </button>
                  )}
                  <Tooltip text={i18n.str`Confirm operation`}>
                    <ButtonBetterBulma type="submit" onClick={update}>
                      <i18n.Translate>Confirm</i18n.Translate>
                    </ButtonBetterBulma>
                  </Tooltip>
                </div>
              </FormProvider>
            </div>
          </div>
        </section>
      </section>
    </div>
  );
}
